// The generic pub/sub primitive. `AppBus` wraps it with this app's event map; nothing else
// constructs one.
//
// ONE dispatch mode, deliberately: `publish`, fire and forget. The template this repo was cut
// from ships three (publish / publishAndWait / publishSequentially) because a todo item's
// persistence had to be awaited by its publisher. Nothing in grading-day does: every write goes
// through a Celeriant handle that the calling store already awaits directly, so an awaited bus
// dispatch would be a second, weaker way to say the same thing. Adding a second mode means
// finding the call site that needs it and updating INVARIANTS rule 9 first.

type EventHandler<T> = (data: T) => void;

export interface EventSubscription {
  unsubscribe: () => void;
}

type EventMap = Record<string, unknown>;

// Narrower than ILogger on purpose: infrastructure/ must not depend on services/. AppBus passes
// the real logger in, so production paths still go through the configured one.
interface BusLogger {
  error(...args: unknown[]): void;
}

export interface SubscribeOptions {
  signal?: AbortSignal;
}

export class EventBus<TEvents extends EventMap> {
  private handlers = new Map<keyof TEvents, Set<EventHandler<unknown>>>();

  constructor(private logger: BusLogger = console) {}

  subscribe<K extends keyof TEvents>(
    event: K,
    handler: EventHandler<TEvents[K]>,
    options?: SubscribeOptions,
  ): EventSubscription {
    if (options?.signal?.aborted === true) return { unsubscribe: () => {} };

    let set = this.handlers.get(event);
    if (set === undefined) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as EventHandler<unknown>);

    let abortListener: (() => void) | undefined;
    const unsubscribe = (): void => {
      const current = this.handlers.get(event);
      if (current !== undefined) {
        current.delete(handler as EventHandler<unknown>);
        if (current.size === 0) this.handlers.delete(event);
      }
      if (abortListener !== undefined && options?.signal !== undefined) {
        options.signal.removeEventListener('abort', abortListener);
        abortListener = undefined;
      }
    };

    if (options?.signal !== undefined) {
      abortListener = unsubscribe;
      options.signal.addEventListener('abort', abortListener, { once: true });
    }

    return { unsubscribe };
  }

  once<K extends keyof TEvents>(
    event: K,
    handler: EventHandler<TEvents[K]>,
    options?: SubscribeOptions,
  ): EventSubscription {
    const subscription = this.subscribe(
      event,
      (data) => {
        // Unsubscribe before invoking, so a handler that re-publishes does not recurse.
        subscription.unsubscribe();
        handler(data);
      },
      options,
    );
    return subscription;
  }

  // A handler that throws must not take the publisher down with it: one broken subscriber would
  // otherwise abort a store method mid-flight and roll back state that did commit.
  publish<K extends keyof TEvents>(event: K, data: TEvents[K]): void {
    const set = this.handlers.get(event);
    if (set === undefined) return;
    for (const handler of [...set] as EventHandler<TEvents[K]>[]) {
      try {
        handler(data);
      } catch (cause) {
        this.logger.error(`[EventBus] handler for "${String(event)}" threw:`, cause);
      }
    }
  }

  // Introspection for the boot check and the narrator (INVARIANTS rules 20 and 21).
  handlerCount<K extends keyof TEvents>(event: K): number {
    return this.handlers.get(event)?.size ?? 0;
  }

  clear(): void {
    this.handlers.clear();
  }
}
